import { t } from 'i18next';

export const CUSTOM_FIELD_TEXT_MIN_LENGTH = 2;
export const CUSTOM_FIELD_TEXT_MAX_LENGTH = 100;
export const CUSTOM_FIELD_TEXTAREA_MAX_LENGTH = 150;

const OPTION_FIELD_TYPES = new Set([
  'multi_select',
  'select_dropdown',
  'select_radio',
]);

type CustomFieldOption = {
  value: string;
  displayText: string;
};

export type CustomFieldConfigForValidation = {
  fieldKey: string;
  fieldName: string;
  fieldType?: string;
  options?: (CustomFieldOption)[] | null;
};

export type ValidateCustomFieldOptions = {
  requiredMessage?: string;
  /** Override the translation function. Inject when calling from non-React contexts (e.g. Flatfile) where i18next is not initialized. */
  tFn?: (key: string) => string;
};


function validateOptionValue(
  field: CustomFieldConfigForValidation,
  trimmedCustomFieldValue: string
): string | null {
  if (!field.options?.length) return null;

  const allowedSet = new Set(field.options.map(option => option.value?.trim().toLowerCase()));
  const optionLabels = field.options
    .map(option => (`${option.displayText} (${option.value})`))
    .join(', ');

  if (field.fieldType === 'multi_select') {
    const parts = trimmedCustomFieldValue.split(',').map(part => part.trim().toLowerCase()).filter(Boolean);
    const hasInvalid = parts.some(part => !allowedSet.has(part));

    return hasInvalid ? `${field.fieldName} must be one or more of the bracketed values: ${optionLabels}` : null;
  }

  return allowedSet.has(trimmedCustomFieldValue.toLowerCase())
    ? null
    : `${field.fieldName} must be one of the bracketed values: ${optionLabels}`;
}

export function validateCustomFieldValue(
  field: CustomFieldConfigForValidation,
  value: string,
  options?: ValidateCustomFieldOptions
): string | null {
  const translate = options?.tFn ?? t;
  const trimmed = String(value ?? '').trim();

  if (trimmed === '') {
    return options?.requiredMessage ?? `${field.fieldName} is required`;
  }

  if (field.fieldType === 'text') {
    if (trimmed.length < CUSTOM_FIELD_TEXT_MIN_LENGTH) {
      return `${field.fieldName} must be at least ${CUSTOM_FIELD_TEXT_MIN_LENGTH} characters`;
    }

    if (trimmed.length > CUSTOM_FIELD_TEXT_MAX_LENGTH) {
      return translate('web.admin.visitorManagement.customField.form.validation.fieldLimitExceeded');
    }

    return null;
  }


  if (field.fieldType && OPTION_FIELD_TYPES.has(field.fieldType)) {
    return validateOptionValue(field, trimmed);
  }

  return null;
}

export type CustomFieldConfigWithId = CustomFieldConfigForValidation & {
  id: string;
};

export type CustomFieldValueItem = {
  customFieldConfigId: string;
  value: string;
};

export type ValidateCustomFieldsForFormOptions = {
  requiredMessage?: string;
  tFn?: (key: string) => string;
};

export function validateCustomFieldsForForm(
  fields: CustomFieldConfigWithId[],
  values: CustomFieldValueItem[],
  formOptions?: ValidateCustomFieldsForFormOptions
): Record<string, string> {
  const errors: Record<string, string> = {};
  const valueByConfigId = new Map(
    values.map(customFieldValue => [customFieldValue.customFieldConfigId, customFieldValue.value])
  );

  for (const field of fields) {
    const value = valueByConfigId.get(field.id) ?? '';
    const error = validateCustomFieldValue(
      {
        fieldKey: field.fieldKey,
        fieldName: field.fieldName,
        fieldType: field.fieldType,
        options: field.options ?? undefined,
      },
      value,
      { requiredMessage: formOptions?.requiredMessage, tFn: formOptions?.tFn }
    );

    if (error) {
      errors[field.fieldKey] = error;
    }
  }

  return errors;
}
